import _ from 'underscore';
import { AppType } from '../../utils';
import util from "../../utils/es5Utils";

// Import version configuration
import { getBlocksForVersion, getAllBlocksForBackwardCompatibility } from './blockVersionConfig';
import { getAllPagesFromContext, getAllTypeWidgetsFromContext, getAllVariablesFromContext, getAllXSightTopicsFromContext } from "./blockVersions/v1/helpers";
export default class ToolboxList {
    constructor({ props }) {
        return this._getToolboxList(props);
    }

    _getSearchFields(widgetsArr) {
        return _.chain(widgetsArr).flatten().reject(item => !_.isString(item)).value();
    }

    _getLibraryBlocks(library) {
        if (!library.blocks) return {};
        return library.blocks.reduce((allBlocks, block) => {
            allBlocks[block.name] = {
                component: block,
                searchTags: [...block.keywords.split(","), block.name]
            };
            return allBlocks;
        }, {});
    }

    // modifies allBlocks in place to remove any blocks that should be excluded based on the app type
    _filterBlocksOnAppType(allBlocks, blocksToExcludeByAppType, appTypeId) {
        const exclusions = blocksToExcludeByAppType[appTypeId];
        const excludedBlockNames = [];
        
        if (!exclusions) return;

        for (const [category, excludedBlocks] of Object.entries(exclusions)) {
            const categoryBlocks = allBlocks[category];

            if (Array.isArray(excludedBlocks)) { // remove any top-level blocks to be excluded
                for (const blockName of excludedBlocks) {
                    // Version-aware exclusion: "SnapActionSubmit" excludes SnapActionSubmit, SnapActionSubmitV2, etc.
                    if (categoryBlocks[blockName]) {
                        // Direct match - exclude this specific block
                        excludedBlockNames.push(categoryBlocks[blockName].component.blockName);
                        delete categoryBlocks[blockName];
                    } else {
                        // Pattern-based exclusion - exclude all versions of this block
                        // Regex matches: blockName, blockNameV2, blockNameV3, etc.
                        const pattern = new RegExp(`^${blockName}(V\\d+)?$`);
                        const blocksToRemove = Object.keys(categoryBlocks).filter(key => pattern.test(key));
                        
                        for (const matchingBlock of blocksToRemove) {
                            excludedBlockNames.push(categoryBlocks[matchingBlock].component.blockName);
                            delete categoryBlocks[matchingBlock];
                        }
                        
                        if (blocksToRemove.length > 0) {
                            console.log(`Pattern exclusion: Removed ${blocksToRemove.length} blocks matching pattern "${blockName}":`, blocksToRemove);
                        }
                    }
                }
            } else if (typeof excludedBlocks === 'object') { // remove any blocks nested in childComponents to be excluded (i.e. in Widget Values)
                for (const [nestedCategory, excludedChildBlocks] of Object.entries(excludedBlocks)) {
                    const childBlocks = categoryBlocks[nestedCategory];

                    for (const childBlockName of excludedChildBlocks) {
                        const childBlock = childBlocks.childComponents[childBlockName];
                        if (childBlock) {
                            excludedBlockNames.push(childBlock.component.blockName);
                            delete childBlocks.childComponents[childBlockName];
                        }
                    }
                }
            }
        }

        this._filterBlocksInMyLibraries(allBlocks, excludedBlockNames);
    }

    // filter out the saved blocks in My Libraries that should be excluded because a nested block has been excluded
    _filterBlocksInMyLibraries(allBlocks, excludedBlockNames) {
        const parser = new DOMParser();

        for (const [_, libraryContents] of Object.entries(allBlocks['My Libraries'])) {
            const blocksToExclude = [];

            for (const [childBlockName, childBlockComponent] of Object.entries(libraryContents.childComponents)) {
                const blockXmlContents = childBlockComponent.component.xml;
                if (!blockXmlContents) continue;

                const blockXml = parser.parseFromString(blockXmlContents, 'text/xml');
                const blockAndShadowElements = blockXml.querySelectorAll('block, shadow');

                for (const element of blockAndShadowElements) {
                    const nestedBlockType = element.getAttribute('type');
                    if (excludedBlockNames.includes(nestedBlockType)) {
                        blocksToExclude.push(childBlockName);
                        break; // stop parsing current block as it has been marked for deletion
                    }
                }
            }

            for (const block of blocksToExclude) {
                delete libraryContents.childComponents[block];
            }

            libraryContents.hasExcludedBlocks = blocksToExclude.length > 0;
        }
    }

    _registerAllBlocks(props, allBlocks) {
        for (const [category, blocks] of Object.entries(allBlocks)) {
            for (const [blockName, blockDef] of Object.entries(blocks)) {
                // add widgets to props (required for blocks that reference widgets)
                let widgets = [];
                try {
                    widgets = util.widgetsOfType ? util.widgetsOfType(props.context, blockName) : [];
                } catch (e) {
                    widgets = [];
                }
                const baseProps = {
                    ...props,
                    widgets
                };

                // register top-level blocks
                if (typeof blockDef.component === 'function') {
                    try {
                        if (blockDef.component.prototype && (blockDef.component.prototype.isReactComponent || blockDef.component.prototype.render)) {
                            // this is a class component - need keyword "new" to initialize
                            new blockDef.component(baseProps);
                        } else if (!blockDef.childComponents) { // skip widget category init - it is not needed here
                            blockDef.component(baseProps);
                        }
                    } catch (e) {
                        console.warn(`Error registering block "${blockName}":`, e);
                    }
                }

                // register child blocks in Widget Values
                if (blockDef.childComponents) {
                    for (const [childName, childDef] of Object.entries(blockDef.childComponents)) {
                        if (typeof childDef.component === 'function') {
                            try {
                                childDef.component(baseProps);
                            } catch (e) {
                                console.warn(`Error registering block "${childName}":`, e);
                            }
                        }
                    }
                }
            }
        }
    }

    // Intelligently add dynamic search tags to version-loaded blocks
    _addDynamicSearchTags(allBlocks, dynamicTags) {
        // Define mapping rules for different types of dynamic tags
        const tagMappings = [
            // Environment Variables
            { 
                category: "Global Variables", 
                blocks: ["SetEnvironmentVariable", "GetEnvironmentVariable"], 
                tags: dynamicTags.envVariables 
            },
            // XSight Topics
            { 
                category: "XSight Topics", 
                blocks: ["SetXSightTopic", "GetXSightTopic"], 
                tags: dynamicTags.xSightTopics 
            }
        ];

        // Widget Values - automatically map widget arrays to their corresponding blocks
        if (allBlocks["Widget Values"]) {
            const widgetMappings = {
                barcode: dynamicTags.barcodeWidgets,
                button: dynamicTags.buttonWidgets,
                checkbox: dynamicTags.checkboxWidgets,
                checkboxlist: dynamicTags.checkboxListWidgets,
                chart: dynamicTags.chartWidgets,
                timer: dynamicTags.timerWidgets,
                date: dynamicTags.dateWidgets,
                dropdown: dynamicTags.dropdownWidgets,
                label: dynamicTags.labelWidgets,
                layout: dynamicTags.layoutWidgets,
                paragraph: dynamicTags.paragraphWidgets,
                rating: dynamicTags.ratingWidgets,
                radiobuttonlist: dynamicTags.radioListWidgets,
                radiobutton: dynamicTags.radioWidgets,
                section: dynamicTags.sectionWidgets,
                image: dynamicTags.imageWidgets,
                fileupload: dynamicTags.fileUploadWidgets,
                textbox: dynamicTags.textboxWidgets,
                tableview: dynamicTags.tableWidgets,
                flipswitch: dynamicTags.flipSwitchWidgets,
                map: dynamicTags.mapWidgets,
                userlist: dynamicTags.userListWidgets,
                businesscard: dynamicTags.businessCardWidgets,
                grouplist: dynamicTags.groupListWidgets
            };

            // Apply widget tags to all child components of each widget type
            Object.entries(widgetMappings).forEach(([widgetType, widgetTags]) => {
                if (allBlocks["Widget Values"][widgetType]?.childComponents && widgetTags) {
                    Object.values(allBlocks["Widget Values"][widgetType].childComponents).forEach(childBlock => {
                        if (childBlock.searchTags) {
                            childBlock.searchTags = [...childBlock.searchTags, ...widgetTags];
                        }
                    });
                }
            });
        }

        // Apply standard category mappings
        tagMappings.forEach(({ category, blocks, tags }) => {
            if (allBlocks[category] && tags) {
                blocks.forEach(blockName => {
                    if (allBlocks[category][blockName]?.searchTags) {
                        allBlocks[category][blockName].searchTags = [
                            ...allBlocks[category][blockName].searchTags,
                            ...tags
                        ];
                    }
                });
            }
        });
    }

    _getToolboxList(props) {
        // Get ServerBuildInfo from context
        const serverBuildInfo = props.context?.ServerBuildInfo;
        
        const pages = this._getSearchFields(getAllPagesFromContext(props.context));
        const barcodeWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "barcode"));
        const buttonWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "button"));
        const checkboxWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "checkbox"));
        const checkboxListWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "checkboxlist"));
        const chartWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "chart"));
        const timerWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "timer"));
        const dateWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "date"));
        const dropdownWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "dropdown"));
        const labelWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "label"));
        const layoutWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "layout"));
        // const listWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "list"));
        const paragraphWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "paragraph"));
        const ratingWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "rating"));
        const radioListWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "radiobuttonlist"));
        const radioWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "radiobutton"));
        const sectionWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "section"));
        const imageWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "image"));
        const fileUploadWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "fileupload"));
        const textboxWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "textbox"));
        const tableWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "tableview"));
        const flipSwitchWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "flipswitch"));
        const mapWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "map"));
        const userListWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "userlist"));
        const businessCardWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "businesscard"));
        const groupListWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "grouplist"));
        const envVariables = this._getSearchFields(getAllVariablesFromContext(props.context));
        const xSightTopics = this._getSearchFields(getAllXSightTopicsFromContext(props.context));

        // blocks to exclude based on app type
        const blocksToExcludeByAppType = {
            [AppType.STANDARD]: {
                'Widget Values': {
                    'user': [
                        'GetUserEmail',
                        'GetUserField',
                        'GetUserFirstName',
                        'GetUserFullName',
                        'GetUserId',
                        'GetUserLastName',
                        'GetUserLoggedIn',
                        'GetUserSystemId'
                    ],
                    'userlist': [
                        'GetUserList'
                    ]
                }
            },
            [AppType.WORKFLOW]: {
                'App Actions': [
                    'SnapActionSync'
                ]
            },
            [AppType.LOCKDOWN]: {
                'Widget Values': {
                    'hamburger': [
                        'SetHamburgerToPage',
                        'GetHamburgerName',
                        'SetHamburgerName',
                        'GetHamburgerImage',
                        'SetHamburgerImage'
                    ]
                },
                'App Actions': [
                    'SnapActionResetPage',
                    'SnapActionSubmit',
                    'SnapActionValidate'
                ],
                'XSight Topics': [
                    'GetXSightTopic',
                    'SetXSightTopic'
                ]
            }
        }

        // Get all blocks for the current version
        // All blocks are now managed by the version config system
        const versionBlocks = getBlocksForVersion(serverBuildInfo) || {};
        
        // Add My Libraries (which are not versioned)
        const allBlocks = {
            ...versionBlocks,
            "My Libraries": 
                props.libraries.reduce((allLibraries, library) => {
                    allLibraries[library.name] = {
                        childComponents: this._getLibraryBlocks(library),
                        hasExcludedBlocks: false
                    };
                    return allLibraries;
                }, {})
        }

        // Add dynamic search tags to version-loaded blocks
        this._addDynamicSearchTags(allBlocks, {
            // Environment and XSight variables
            envVariables,
            xSightTopics,
            // Widget arrays for Widget Values blocks
            barcodeWidgets, buttonWidgets, checkboxWidgets, checkboxListWidgets, chartWidgets, timerWidgets,
            dateWidgets, dropdownWidgets, labelWidgets, layoutWidgets, paragraphWidgets, ratingWidgets,
            radioListWidgets, radioWidgets, sectionWidgets, imageWidgets, fileUploadWidgets, textboxWidgets,
            tableWidgets, flipSwitchWidgets, mapWidgets, userListWidgets, businessCardWidgets, groupListWidgets
        });

        // register all possible blocks from all versions (backward compatibility, required so that loading saved scripts does not crash Blockly)
        const allVersionBlocks = getAllBlocksForBackwardCompatibility();
        this._registerAllBlocks(props, { ...allBlocks, ...allVersionBlocks });

        // remove any blocks to be excluded from allBlocks in place
        this._filterBlocksOnAppType(allBlocks, blocksToExcludeByAppType, props.context.appTypeId);

        return allBlocks;
    }
}
